import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Users, Clock, Briefcase, Building, MessageSquare, Star as IdeologyIcon, Feather } from 'lucide-react'; // Added more icons
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.tsx"; // Added CardHeader & CardTitle
import photo from '../assets/land6.jpg'

const About = () => {
  const { t } = useTranslation();

  // Stats can be dynamic or translated if values change per language
  const stats = [
    { icon: Users, value: "150", label: t('companyProfile.teamTitle') }, // Using "Our Team" as label for staff count
    { icon: Briefcase, value: "40+", label: t('about.yearsExperience') }, // Original key for years
    { icon: Clock, value: "Since 1980", label: t('footer.craftingSince') }, // Referencing footer or a new key
  ];

  return (
    <section id="about" className="relative py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4">
        {/* Main About Us Title from Nav */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            {t('nav.about')} {/* "About Us" */}
          </h2>
          <p className="text-xl text-amber-600 font-medium">
            {t('hero.subtitle')} {/* "Decorative Furniture Since 1980" */}
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto space-y-16">

          {/* History and Background Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="text-3xl text-amber-700">{t('companyProfile.historyTitle')}</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>{t('companyProfile.historyP1')}</p>
                <p>{t('companyProfile.historyP2')}</p>
                <p>{t('companyProfile.historyP3')}</p>
                <p>{t('companyProfile.historyP4')}</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Chairman's Message Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Card className="shadow-xl bg-amber-50">
              <CardHeader>
                <CardTitle className="text-3xl text-amber-700">{t('companyProfile.chairmanTitle')}</CardTitle>
                <p className="text-md text-gray-600">{t('companyProfile.chairmanName')}</p>
              </CardHeader>
              <CardContent className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>{t('companyProfile.chairmanSpeechP1')}</p>
                <p>{t('companyProfile.chairmanSpeechP2')}</p>
                <p>{t('companyProfile.chairmanSpeechP3')}</p>
                <p className="text-right font-semibold">{t('companyProfile.chairmanSignature')}</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Our Ideology Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center"
          >

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img
                  src={photo}
                  alt={t('about.ourShowroom')}
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>

              {/* Floating Card */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="absolute -bottom-6 -left-6 right-6"
              >
                <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-xl">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800 mb-1">
                          {t('about.location')}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {t('companyProfile.alexOfficeAddress')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>

            <div className="order-1 md:order-2">
              <h3 className="text-3xl font-bold text-amber-700 mb-4 flex items-center">
                <IdeologyIcon className="w-8 h-8 mr-3 text-amber-600" />
                {t('companyProfile.ideologyTitle')}
              </h3>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-3">
                <p>{t('companyProfile.ideologyP1')}</p>
                <p>{t('companyProfile.ideologyP2')}</p>
              </div>
            </div>
          </motion.div>




          {/* Our Team Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-3xl text-amber-700 flex items-center">
                  <Users className="w-8 h-8 mr-3 text-amber-600" />
                  {t('companyProfile.teamTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-lg max-w-none text-gray-700 space-y-3">
                <p>{t('companyProfile.teamP1')}</p>
                <p>{t('companyProfile.teamP2')}</p>
                <p>{t('companyProfile.teamP3')}</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Stats Section (if you want to keep it) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 pt-6 text-center">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="font-bold text-3xl text-gray-800">{stat.value}</div>
                <div className="text-md text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>


          {/* Showrooms Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h3 className="text-3xl font-bold text-amber-700 mb-6 text-center flex items-center justify-center">
              <Building className="w-8 h-8 mr-3 text-amber-600" />
              {t('companyProfile.showroomsTitle')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-3">
                    <MapPin className="w-7 h-7 text-amber-600" />
                    <h4 className="font-semibold text-xl text-gray-800">{t('companyProfile.alexOfficeAddress').split(':')[0]}</h4>
                  </div>
                  <p className="text-gray-600">{t('companyProfile.showroomsP1')}</p>
                </CardContent>
              </Card>
              <Card className="shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-3">
                    <MapPin className="w-7 h-7 text-amber-600" />
                    <h4 className="font-semibold text-xl text-gray-800">Main Showrooms</h4>
                  </div>
                  <p className="text-gray-600">{t('companyProfile.showroomsP2')}</p>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Communication and Events Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="text-3xl text-amber-700 flex items-center">
                  <MessageSquare className="w-8 h-8 mr-3 text-amber-600" />
                  {t('companyProfile.eventsTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>{t('companyProfile.eventsP1')}</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>{t('companyProfile.furnex2009')}</li>
                  <li>{t('companyProfile.furnex2010')}</li>
                  <li>{t('companyProfile.furnex2013')}</li>
                  <li>{t('companyProfile.houseAlex2014')}</li>
                  <li>{t('companyProfile.furnex2016')}</li>
                  <li>{t('companyProfile.houseAlex2020')}</li>
                </ul>
              </CardContent>
            </Card>
          </motion.div>

          {/* Company Summary Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Card className="shadow-xl bg-amber-50">
              <CardHeader>
                <CardTitle className="text-3xl text-amber-700 flex items-center">
                  <Feather className="w-8 h-8 mr-3 text-amber-600" />
                  {t('companyProfile.summaryTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-lg max-w-none text-gray-700 space-y-4">
                <p>{t('companyProfile.summaryP1')}</p>
                <p>{t('companyProfile.summaryP2')}</p>
              </CardContent>
            </Card>
          </motion.div>


        </div>
      </div>
    </section>
  );
};

export default About;